import React, { useState } from 'react';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import Card from '@components/framework/card/Card.tsx';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import { issuesForOrder, loadableOrdersFrom, RouteOrderIssue } from '@app/pages/expedition/create/routeRules.ts';
import OrderIssueMessage from '@app/pages/expedition/create/components/OrderIssueMessage.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { emptyOrder, Order } from '@components/framework/orders/common.tsx';
import { OrdersConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { isLadenFactorEligibleByOrders } from '@utils/expeditionUtils.ts';
import { Form } from '@hooks/useForm/useForm.tsx';
import { ExpeditionLocality, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { RouteTestIds } from '@app/pages/expedition/create/testIds.ts';
import UnloadingSection from '@app/pages/expedition/create/components/UnloadingSection.tsx';
import MoveExistingOrderButton from '@app/pages/expedition/create/components/MoveExistingOrderButton.tsx';
import MoveOrderLoadingButton from '@app/pages/expedition/create/components/MoveOrderLoadingButton.tsx';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  locIndex: number;
  location: ExpeditionLocality;
  locationCount: number;
  ordersConfig: OrdersConfig;
  companyId: string;
  testIds: RouteTestIds;
  onDeleteUnloading: (locIndex: number) => void;
  unloadableOrders: GlobalOrdersRequest[];
  onUnloadOrder: (locIndex: number, orderId: string) => void;
  onOrderAdded: (locIndex: number, orderId: string) => void;
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  orderIssues: RouteOrderIssue[];
}

/** What a stop loads and unloads: the cargo editor on one side, order assignments on the other. */
const LocationOrdersPanel: React.FC<ComponentProps> = ({
                                                         form,
                                                         locIndex,
                                                         location: loc,
                                                         locationCount,
                                                         ordersConfig,
                                                         companyId,
                                                         testIds,
                                                         onDeleteUnloading,
                                                         unloadableOrders,
                                                         onUnloadOrder,
                                                         onOrderAdded,
                                                         onMoveOrderLoading,
                                                         orderIssues,
                                                       }) => {
  const { tr } = useTranslationLgs();
  const isLastLocation = locIndex === locationCount - 1;
  const loadableOrders = loadableOrdersFrom(form.values.locations, locIndex);

  // Fold state is deliberately local: keeping it on the page re-rendered the whole form on every
  // open/close, which made the location card, attachments and carrier panels refetch.
  const [unloadingOpened, setUnloadingOpened] = useState(false);

  // A stop showing unloading assignments must show the area holding them, and the last stop always
  // unloads whatever is left — so those cases are derived rather than remembered.
  const unloadingUnfolded = isLastLocation || unloadingOpened || (loc.unloadingOrderIds?.length ?? 0) > 0;

  return (
    <div className={'flex flex-col gap-4'}>
      {loc.orders?.length > 0 ? (
        <Card>
            <OrdersComponent
              config={ordersConfig}
              canBeEmpty={true}
              generateOrderId={true}
              onChange={(ordersList: Order[]) => {
                const currentOrderIds = (form.values.locations[locIndex].orders || []).map(o => o.id);
                const newOrderIds = ordersList.map(o => o.id).filter(id => !currentOrderIds.includes(id));
                const removedOrderIds = currentOrderIds.filter(id => !ordersList.some(o => o.id === id));

                form.useProduce(draft => {
                  draft.locations[locIndex].orders = ordersList;

                  // A deleted order no longer exists anywhere, so the stops that were unloading it
                  // must let go of it too — otherwise it lingers as an assignment with nothing
                  // behind it and no way to clear it.
                  if (removedOrderIds.length > 0) {
                    draft.locations.forEach(location => {
                      if (location.unloadingOrderIds) {
                        location.unloadingOrderIds = location.unloadingOrderIds.filter(id => !removedOrderIds.includes(id));
                      }
                    });
                  }

                  if (form.values.distribution && isLadenFactorEligibleByOrders(ordersList)) {
                    draft.distribution.ladenFactor = true;
                    draft.distribution.allowPartialDelivery = true;
                  }
                });

                // After the produce, never inside it: `useProduce` ends in `overwriteValues`, which
                // replaces the whole form and would discard anything queued before it.
                newOrderIds.forEach(orderId => onOrderAdded(locIndex, orderId));
              }}
              validationErrors={form.validationErrors
                .filter(t => t.path.startsWith(`locations[${locIndex}].orders`))
                .map(t => ({ ...t, path: t.path.replace(`locations[${locIndex}].orders`, 'orders') }))}
              // @ts-ignore
              orders={loc.orders}
              companyId={companyId}
              dataTestIdPrefix={testIds.locationLoading}
              footerActions={
                !isLastLocation && loadableOrders.length > 0 ? (
                  <MoveExistingOrderButton
                    locIndex={locIndex}
                    loadableOrders={loadableOrders}
                    onMoveOrderLoading={onMoveOrderLoading}
                    dataTestIdPrefix={testIds.locationLoading}
                  />
                ) : null
              }
              orderHeaderActions={order => (
                <MoveOrderLoadingButton
                  orderId={order.id}
                  locIndex={locIndex}
                  locations={form.values.locations}
                  onMoveOrderLoading={onMoveOrderLoading}
                  dataTestId={`${testIds.locationLoading}-moveLoading`}
                />
              )}
              orderBanner={order => (
                <OrderIssueMessage
                  issues={issuesForOrder(orderIssues, order.id)}
                  action={
                    <MoveOrderLoadingButton
                      orderId={order.id}
                      locIndex={locIndex}
                      locations={form.values.locations}
                      onMoveOrderLoading={onMoveOrderLoading}
                      label={tr('CreateExpedition.moveOrderAction', 'Move')}
                      dataTestId={`${testIds.locationLoading}-orderIssue-move`}
                    />
                  }
                  dataTestId={`${testIds.locationLoading}-orderIssue`}
                />
              )}
            />
        </Card>
      ) : (
        <>
          {!isLastLocation && (
            <div className={'flex flex-col gap-4'}>
              <RowButton
                title={tr('CreateExpedition.nakladka', 'Loading')}
                onClick={() => {
                  const newOrder = emptyOrder(ordersConfig.cargo.required, true);
                  const currentOrders = form.values.locations[locIndex].orders || [];

                  form.useProduce(draft => {
                    draft.locations[locIndex].orders = [...currentOrders, newOrder];
                    if (draft.distribution && newOrder.items.find(item => item.amount > 0)) {
                      draft.distribution.ladenFactor = true;
                      draft.distribution.allowPartialDelivery = true;
                    }
                  });

                  // The order is loaded here, so this stop is not a candidate for unloading it.
                  onOrderAdded(locIndex, newOrder.id);
                }}
                dataTestId={testIds.locationLoading}
              />
            </div>
          )}
        </>
      )}

      <div className={'flex flex-row gap-4'}>
        {!unloadingUnfolded && locIndex !== 0 ? (
          <RowButton
            title={tr('CreateExpedition.vykladka', 'Unloading')}
            onClick={() => setUnloadingOpened(true)}
            dataTestId={testIds.locationUnloading}
          />
        ) : (
          <>
            {locIndex !== 0 && (
              <Card>
                <UnloadingSection
                  locIndex={locIndex}
                  location={loc}
                  locations={form.values.locations}
                  canDelete={!isLastLocation}
                  onDelete={() => {
                    // Clearing the assignments is only half of it — the area is also shown because
                    // the user opened it, so that has to be undone too.
                    setUnloadingOpened(false);
                    onDeleteUnloading(locIndex);
                  }}
                  unloadableOrders={unloadableOrders}
                  onUnloadOrder={onUnloadOrder}
                  orderIssues={orderIssues}
                  dataTestIdPrefix={testIds.locationUnloading}
                />
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LocationOrdersPanel;